import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  QuestionnaireAnswers,
  AnswerMeta,
  Question,
  QuestionType,
  FamilyHistoryCondition,
} from "./types";

interface UseQuestionnaireStateProps {
  questions: Question[];
  initialAnswers: QuestionnaireAnswers;
  onSubmit: (answers: any[]) => void | Promise<void>;
  onClose?: () => void;
}

// Extract selected options from multi-select answers (object with boolean values)
// Returns array of keys where value is true
const getInitialSelectedOptions = (answer: any): string[] => {
  if (!answer?.meta?.value) return [];
  const value = answer.meta.value;
  if (typeof value === "object") {
    return Object.keys(value).filter((key) => value[key] === true);
  }
  return [];
};

// Extract selected option from single-select answers (string value)
const getInitialSelectedOption = (answer: any): string => {
  if (!answer?.meta?.value) return "";
  const value = answer.meta.value;
  if (typeof value === "string") return value;
  return "";
};

// Manages questionnaire state and navigation for both single-select (radio) and multi-select (checkbox) questions
export function useQuestionnaireState({
  questions,
  initialAnswers,
  onSubmit,
  onClose,
}: UseQuestionnaireStateProps) {
  const navigate = useNavigate();
  const topRef = useRef<HTMLDivElement>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<QuestionnaireAnswers>(initialAnswers);

  // For single-select questions: stores the selected value as a string
  const [selectedOption, setSelectedOption] = useState(
    getInitialSelectedOption(initialAnswers[questions[0]?.type])
  );

  // For multi-select questions: stores array of selected option keys
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    getInitialSelectedOptions(initialAnswers[questions[0]?.type])
  );

  // Special state for INTENSE_EXERCISE question's strength training checkbox
  const [strengthTraining, setStrengthTraining] = useState(
    initialAnswers[questions[0]?.type]?.meta.performedStrengthExercises || false
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];

  const buildAnswerMeta = (
    value: string | Record<string, boolean>,
    includeStrengthTraining?: boolean
  ): AnswerMeta => {
    const meta: AnswerMeta = {
      value,
    };

    if (includeStrengthTraining !== undefined) {
      meta.performedStrengthExercises = includeStrengthTraining;
    }

    return meta;
  };

  // Navigate to next question or submit if on last question
  const handleNext = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Navigate to next question
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);

      // Restore previously selected answers for this question (if any)
      setSelectedOption(
        getInitialSelectedOption(answers[questions[nextIndex].type])
      );
      setSelectedOptions(
        getInitialSelectedOptions(answers[questions[nextIndex].type])
      );
      setStrengthTraining(
        answers[questions[nextIndex].type]?.meta.performedStrengthExercises ||
          false
      );

      // Scroll to top of questionnaire
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    } else {
      setIsSubmitting(true);
      try {
        const answersArray = Object.values(answers);
        await Promise.resolve(onSubmit(answersArray));
      } catch (error) {
        console.error("Error submitting questionnaire:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Navigate to previous question or close if on first question
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      // Navigate to previous question
      const previousIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(previousIndex);

      // Restore previously selected answers for this question
      setSelectedOption(
        getInitialSelectedOption(answers[questions[previousIndex].type])
      );
      setSelectedOptions(
        getInitialSelectedOptions(answers[questions[previousIndex].type])
      );
      setStrengthTraining(
        answers[questions[previousIndex].type]?.meta
          .performedStrengthExercises || false
      );
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    } else {
      handleClose();
    }
  };

  // Handle radio button selection (includes special logic for INTENSE_EXERCISE strength training)
  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSelectedOption(newValue);

    // Special logic for INTENSE_EXERCISE question
    const isExerciseQuestion =
      currentQuestion.type === QuestionType.INTENSE_EXERCISE;
    const secondOptionValue = currentQuestion.meta?.values[1];
    const shouldResetStrengthTraining =
      isExerciseQuestion && newValue !== secondOptionValue;

    // Reset strength training checkbox if user selects option other than second one
    if (shouldResetStrengthTraining) {
      setStrengthTraining(false);
    }

    // Build answer metadata, including strength training flag for exercise question
    const answerMeta = isExerciseQuestion
      ? buildAnswerMeta(
          newValue,
          shouldResetStrengthTraining ? false : strengthTraining
        )
      : buildAnswerMeta(newValue);

    // Update answers state with new selection
    const updatedAnswers: QuestionnaireAnswers = {
      ...answers,
      [currentQuestion.type]: {
        type: currentQuestion.type,
        meta: answerMeta,
      },
    };

    setAnswers(updatedAnswers);
  };

  // Handle strength training checkbox for INTENSE_EXERCISE question
  const handleStrengthTrainingChange = (checked: boolean) => {
    setStrengthTraining(checked);

    const answerMeta = buildAnswerMeta(selectedOption, checked);

    const updatedAnswers: QuestionnaireAnswers = {
      ...answers,
      [currentQuestion.type]: {
        type: currentQuestion.type,
        meta: answerMeta,
      },
    };

    setAnswers(updatedAnswers);
  };

  // Handle checkbox selection for multi-select questions
  // Stores all options as object with boolean values
  const handleCheckboxChange = (value: string, checked: boolean) => {
    let newSelectedOptions: string[];

    if (checked) {
      // If "NONE" is selected, deselect all other options
      if (value === FamilyHistoryCondition.NONE) {
        newSelectedOptions = [FamilyHistoryCondition.NONE];
      } else {
        // If any other option is selected, deselect "NONE"
        newSelectedOptions = [
          ...selectedOptions.filter(
            (option) => option !== FamilyHistoryCondition.NONE
          ),
          value,
        ];
      }
    } else {
      newSelectedOptions = selectedOptions.filter((option) => option !== value);
    }

    setSelectedOptions(newSelectedOptions);

    // Build object with all options as keys and boolean values
    const allOptions = currentQuestion.meta?.values || [];
    const valueObject: Record<string, boolean> = {};
    allOptions.forEach((option) => {
      valueObject[option] = newSelectedOptions.includes(option);
    });

    const answerMeta = buildAnswerMeta(valueObject);

    // Update answers state with new selections
    const updatedAnswers: QuestionnaireAnswers = {
      ...answers,
      [currentQuestion.type]: {
        type: currentQuestion.type,
        meta: answerMeta,
      },
    };

    setAnswers(updatedAnswers);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  return {
    topRef,
    currentQuestionIndex,
    currentQuestion,
    selectedOption,
    selectedOptions,
    strengthTraining,
    isSubmitting,
    handleNext,
    handlePrevious,
    handleOptionChange,
    handleCheckboxChange,
    handleStrengthTrainingChange,
    handleClose,
  };
}
